/**
 * Shared frontend logger.
 *
 *   logger.info("Cache cleared", { ctx: ... });
 *   logger.warn("CSV import skipped rows", { errors });
 *   logger.error("Trip save failed", err, { tripId });
 *
 * Always mirrors to console.* so dev experience is unchanged.
 * Additionally ships warn+error entries to POST /api/client-logs in batches
 * (debounced 1s, capped 25 entries) — non-blocking and silent on failure.
 *
 * Also installs window.onerror + onunhandledrejection handlers so uncaught
 * exceptions in production are captured automatically (no try/catch needed).
 *
 * Easy to swap to Sentry / Datadog later by replacing the `flush()` body.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND_URL}/api/client-logs`;
const FLUSH_DEBOUNCE_MS = 1000;
const MAX_BATCH = 25;
const MAX_QUEUE = 200;

// Levels that get shipped to the server. Info/debug stay local-only by default.
const SHIPPED_LEVELS = new Set(["warn", "error"]);

let queue = [];
let flushTimer = null;
// Bare axios instance (no /api baseURL, no withCredentials interceptors) —
// we don't want logger calls to recurse via the main `api` instance's hooks.
const transport = axios.create({ timeout: 5000 });

function _enqueue(entry) {
  if (queue.length >= MAX_QUEUE) {
    // Drop oldest so a tight error-loop can't unbounded-grow memory.
    queue.shift();
  }
  queue.push(entry);
  if (!flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_DEBOUNCE_MS);
  }
}

async function flush() {
  flushTimer = null;
  if (!queue.length) return;
  const entries = queue.splice(0, MAX_BATCH);
  const payload = {
    entries,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
  };
  try {
    // Attach Bearer if we have one — non-critical, server tolerates missing.
    const headers = {};
    try {
      const t = localStorage.getItem("session_token");
      if (t) headers.Authorization = `Bearer ${t}`;
    } catch {
      /* localStorage unavailable — proceed unauthenticated */
    }
    await transport.post(ENDPOINT, payload, { headers, withCredentials: true });
  } catch {
    // Never surface logger errors to the app. Re-queue would risk a loop, so drop.
  }
  // If the queue grew during the await, schedule another flush.
  if (queue.length && !flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_DEBOUNCE_MS);
  }
}

function _serialiseError(err) {
  if (!err) return {};
  if (err instanceof Error) {
    return {
      name: err.name,
      message: err.message,
      stack: typeof err.stack === "string" ? err.stack.slice(0, 2000) : undefined,
    };
  }
  if (typeof err === "object") {
    // Axios errors: keep useful slices
    return {
      message: err.message,
      status: err.response?.status,
      data: err.response?.data,
      url: err.config?.url,
    };
  }
  return { value: String(err) };
}

function _log(level, message, errOrCtx, maybeCtx) {
  // Resolve overload: log(level, msg, ctx) vs log(level, msg, err, ctx)
  let context = {};
  if (errOrCtx instanceof Error || (errOrCtx && errOrCtx.response)) {
    context = { error: _serialiseError(errOrCtx), ...(maybeCtx || {}) };
  } else if (errOrCtx && typeof errOrCtx === "object") {
    context = errOrCtx;
  }

  // Always mirror to console — keeps DevTools UX intact.
  const consoleFn = console[level] || console.log;
  if (Object.keys(context).length) consoleFn(`[${level}] ${message}`, context);
  else consoleFn(`[${level}] ${message}`);

  if (!SHIPPED_LEVELS.has(level)) return;

  _enqueue({
    level,
    message: String(message),
    context,
    url: typeof window !== "undefined" ? window.location.pathname : undefined,
    ts: new Date().toISOString(),
  });
}

const logger = {
  debug: (msg, ctx) => _log("debug", msg, ctx),
  info: (msg, ctx) => _log("info", msg, ctx),
  warn: (msg, errOrCtx, ctx) => _log("warn", msg, errOrCtx, ctx),
  error: (msg, errOrCtx, ctx) => _log("error", msg, errOrCtx, ctx),
  /** Force-flush — useful before navigation or beforeunload. */
  flush,
};

let _installed = false;

/**
 * Install global error handlers. Call once at app boot (in index.js).
 * Safe to call multiple times — idempotent.
 */
export function installGlobalErrorHandlers() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;

  // CAPTURE-phase suppressor for the harmless "ResizeObserver loop completed"
  // browser warning. The browser raises this when a ResizeObserver callback
  // triggers a layout that the observer can't deliver synchronously — common
  // with cmdk + Radix Popover, Recharts ResponsiveContainer, etc. It is NOT
  // a real error and Chrome itself flags it as "spec-compliant noise" since
  // 2019. CRA's react-error-overlay (dev only) listens in bubble phase and
  // shows a full-screen red panel when it sees this. We install our listener
  // in capture phase + stopImmediatePropagation so the overlay never sees it.
  //
  // Production builds don't ship react-error-overlay at all, so this is
  // strictly a dev-UX cleanup — but it also prevents these benign entries
  // from polluting /api/client-logs.
  const RESIZE_OBSERVER_RE = /ResizeObserver loop/i;
  window.addEventListener(
    "error",
    (ev) => {
      const msg = ev?.message || ev?.error?.message || "";
      if (msg && RESIZE_OBSERVER_RE.test(msg)) {
        ev.stopImmediatePropagation();
        ev.preventDefault();
        return false;
      }
    },
    true, // capture phase — runs before the dev overlay's bubble-phase listener
  );

  window.addEventListener("error", (ev) => {
    // Bubble-phase shipper. ResizeObserver noise was already swallowed above,
    // but re-check defensively in case the capture-phase early-exit was missed.
    if (ev?.message && RESIZE_OBSERVER_RE.test(ev.message)) return;
    logger.error("window.onerror", ev?.error || ev?.message, {
      filename: ev?.filename,
      lineno: ev?.lineno,
      colno: ev?.colno,
    });
  });

  window.addEventListener("unhandledrejection", (ev) => {
    const msg = ev?.reason?.message || String(ev?.reason || "");
    if (msg && RESIZE_OBSERVER_RE.test(msg)) return;
    logger.error("unhandledrejection", ev?.reason);
  });

  // Best-effort flush on tab close.
  window.addEventListener("beforeunload", () => {
    try {
      flush();
    } catch {
      /* nothing we can do mid-unload */
    }
  });
}

export default logger;
